import type { MyInvite } from "../../app/hooks/useMyInvites.js";

const LINK_TYPE_LABEL: Record<string, string> = {
  label: "Label",
  management: "Management",
  booking: "Booking",
};

/**
 * The one sentence a pending invite reads as, in the Pending invites modal (ART-239). It names the PERSON who sent
 * it and what it is for — "Dana Cole invited you to join Midnight Freight as Tour Manager" — because a tenant name
 * alone ("Midnight Freight invited you to the band") does not tell you who to ask about it. A partner invite is
 * worded as what it IS: following someone's schedule, not joining anything.
 */
export function inviteSentence(invite: MyInvite): string {
  const tenant = invite.counterpartyName || "a team";
  const who = invite.inviterName || invite.counterpartyName || "Someone";
  const asTitle = invite.titleLabel ? ` as ${invite.titleLabel}` : "";
  switch (invite.kind) {
    case "band_seat":
    case "org_seat":
      // An invite from a band's ARTIST account is sent by the band itself ("the Artist IS the band": that account
      // carries the band's name), so naming both would read "Midnight Freight invited you to join Midnight Freight".
      return who === tenant
        ? `${who} invited you to join their ${invite.kind === "org_seat" ? "team" : "crew"}${asTitle}`
        : `${who} invited you to join ${tenant}${asTitle}`;
    case "partner":
      return `${who} wants you to keep up with their schedule`;
    case "org_link": {
      const t = invite.linkType ? LINK_TYPE_LABEL[invite.linkType] ?? invite.linkType : null;
      return t ? `${tenant} wants to connect · ${t}` : `${tenant} wants to connect`;
    }
    default:
      return `${tenant} sent you an invite`;
  }
}

/** Initials for a band or org counterparty (rounded square), never a person's. */
export function tenantInitials(name: string | null | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words.slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}
